import { MatoItem } from "@api/throughShipment/throughShipmentApi";
import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { uploadMedia } from "@api/uploadMediaApi";
import { Button, message, Modal, Row, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import type { UploadFile } from "antd/lib/upload/interface";
import type { UploadRequestOption as RcCustomRequestOptions } from "rc-upload/lib/interface";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Row as StyledRow } from "./OrderDisplayComponent/style";

const MATO_DIR_PREFIX = "external_order/mato/";
const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILE_COUNT = 50;
const FILE_NAME_REGEX = /:ts:\d+:ts:$/;

const UploadNotice = styled.div`
  font-size: 14px;
  margin-left: 16px;
  color: ${({ theme }) => theme.colorSecondary500};
`;

const EmptyText = styled.span`
  color: ${({ theme }) => theme.colorNeutral500};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const FileList = styled(StyledRow)`
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`;

const FileLink = styled.a`
  margin-right: 12px;
`;

type Props = {
  onUpload?: React.Dispatch<React.SetStateAction<MatoItem[]>>;
  value?: MatoItem[];
  isSend?: boolean;
};

function getMatoPreviewUrl(item: MatoItem): string {
  if (item.path?.startsWith("http")) return item.path;
  const base = process.env.REACT_APP_CLOUDFLARE_DOMAIN || "";
  const path = item.path || `${item.matoDirPrefix || ""}${item.matoFilePath}`;
  return path.startsWith("http") ? path : `${base}media/${path}`;
}

function matoLToFileList(matoL: MatoItem[], blobUrlMap: Map<string, string>): UploadFile[] {
  return matoL
    .filter((item) => !!item.matoFilePath)
    .map((item, index) => ({
      uid: `mato-${index}-${item.matoFilePath}`,
      name: (item.matoFilePath.split("/").pop() || "已上傳檔案").replace(FILE_NAME_REGEX, ""),
      status: "done",
      url: blobUrlMap.get(item.matoFilePath),
    }));
}

const UploadFileButton = (props: Props) => {
  const { onUpload, value = [], isSend } = props;
  const blobUrlMapRef = useRef<Map<string, string>>(new Map());
  const [fileList, setFileList] = useState<UploadFile[]>(() => matoLToFileList(value, blobUrlMapRef.current));
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);

  useEffect(() => {
    setFileList(matoLToFileList(value, blobUrlMapRef.current));
  }, [value]);

  useEffect(() => {
    const blobUrlMap = blobUrlMapRef.current;
    return () => {
      blobUrlMap.forEach((url) => URL.revokeObjectURL(url));
      blobUrlMap.clear();
    };
  }, []);

  const beforeUpload = (file: RcFile, currentFileList: RcFile[]) => {
    const totalCount = fileList.length + uploadingFiles.length + currentFileList.length;
    if (totalCount > MAX_FILE_COUNT) {
      message.warning(`最多只能上傳 ${MAX_FILE_COUNT} 個檔案`);
      return Upload.LIST_IGNORE;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.warning("僅限上傳 PDF、PNG、JPEG 格式");
      return Upload.LIST_IGNORE;
    }
    if (file.size && file.size > MAX_FILE_SIZE) {
      message.warning("每個檔案不得超過 5 MB");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleRemove = (file: UploadFile) => {
    const isUploading = uploadingFiles.some((f) => f.uid === file.uid);
    if (isUploading) {
      setUploadingFiles((prev) => prev.filter((f) => f.uid !== file.uid));
      return;
    }

    Modal.confirm({
      title: "你確定要刪除嗎?",
      icon: <ExclamationCircleOutlined />,
      okText: "是",
      cancelText: "否",
      onOk() {
        const index = fileList.findIndex((f) => f.uid === file.uid);
        if (index >= 0) {
          const matoFilePath = value[index]?.matoFilePath;
          if (matoFilePath) {
            const blobUrl = blobUrlMapRef.current.get(matoFilePath);
            if (blobUrl) {
              URL.revokeObjectURL(blobUrl);
              blobUrlMapRef.current.delete(matoFilePath);
            }
          }
          const newMatoL = value.filter((_, i) => i !== index);
          setFileList(matoLToFileList(newMatoL, blobUrlMapRef.current));
          onUpload?.(newMatoL);
          message.success("刪除成功");
        }
      },
    });
  };

  const customRequest = async (options: RcCustomRequestOptions) => {
    const { file, onSuccess, onError } = options;
    const { uid } = file as UploadFile;
    const uploadFile: UploadFile = {
      uid,
      name: (file as File).name,
      status: "uploading",
    };
    setUploadingFiles((prev) => prev.concat(uploadFile));

    try {
      const result = await uploadMedia(file as File);
      const blobUrl = URL.createObjectURL(file as File);
      blobUrlMapRef.current.set(result, blobUrl);
      const newItem: MatoItem = {
        matoDirPrefix: MATO_DIR_PREFIX,
        matoFilePath: result,
      };
      const newFileItem: UploadFile = {
        uid: `mato-${result}`,
        name: (file as File).name,
        status: "done",
        url: blobUrl,
      };
      setFileList((prev) => prev.concat(newFileItem));
      onUpload?.((prev) => prev.concat(newItem));
      if (onSuccess) onSuccess({ matoFilePath: result }, file as any);
    } catch (error: any) {
      message.warning(`上傳失敗：${error.message}`);
      if (onError) onError(error);
    } finally {
      setUploadingFiles((prev) => prev.filter((f) => f.uid !== uid));
    }
  };

  const displayFileList = fileList.concat(uploadingFiles);

  if (isSend) {
    return (
      <Container>
        {value.length > 0 ? (
          <FileList marginTop="8px">
            {value.map((item) => {
              const url = getMatoPreviewUrl(item);
              return (
                <FileLink key={item.id ?? item.matoFilePath} href={url} target="_blank" rel="noopener noreferrer">
                  {item.name}
                </FileLink>
              );
            })}
          </FileList>
        ) : (
          <EmptyText>無上傳麥頭</EmptyText>
        )}
      </Container>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
      <Upload
        fileList={displayFileList}
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        onRemove={handleRemove}
        multiple
        maxCount={MAX_FILE_COUNT}
        showUploadList={{ showRemoveIcon: true }}
      >
        <Row align="middle">
          <Button icon={<UploadOutlined />}>點擊上傳</Button>
          <UploadNotice>*僅限 PDF/PNG/JPEG，每個檔案不超過 5 MB，最多 50 個檔案</UploadNotice>
        </Row>
      </Upload>
    </div>
  );
};

export default UploadFileButton;
